import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment-timezone";
import { fetchUserStatus, enterContest } from "../../api/user";

const initialState = {
  loading: true,
  backgroundImage: "none",
  tz: "",
  userId: "",
  playStartsAt: 0,
  currentStage: "group",
  contestType: "World Cup",
  entryPage: "group",
  focus: "",
  upcomingGames: [],
  hasLiveGame: false,
  score: {
    earned_score: 0,
    missed_score: 0,
    max_score: 0,
    total_picks: 0,
    correct_picks: 0,
    missed_picks: 0,
  },
  scoring: null,
  picksProgress: {
    completedGroups: {
      A: false,
      B: false,
      C: false,
      D: false,
      E: false,
      F: false,
      G: false,
      H: false,
      I: false,
      J: false,
      K: false,
      L: false,
    },
    allGroupsComplete: false,
    thirdPlaceComplete: false,
    groupStageComplete: false,
    completedKORounds: {
      round32: false,
      round16: false,
      quarterFinal: false,
      semiFinal: false,
      final: false,
      thirdPlace: false,
    },
    tiebreakerAnswer: -1,
    allPicksComplete: false,
    picksSubmitted: false,
  },
};

export const setBackgroundImageAction = createAsyncThunk(
  "app/setBackgroundImage",
  async (image) => {
    return { backgroundImage: image };
  },
);

export const getStatusAction = createAsyncThunk(
  "app/getStatus",
  async (props, { rejectWithValue }) => {
    const { spotlightId, userId, contestId } = props;

    try {
      const res = await fetchUserStatus({
        user_id: userId,
        spotlight_uuid: spotlightId,
        contest_id: contestId,
      });

      return res;
    } catch (err) {
      console.error("Error in [getStatus] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

// Marks the user as having entered the contest — this is what the "Start
// Making Picks" button on Overview fires. Distinct from any pick-completion
// flag: it's what flips entry_page away from "overview" on the NEXT visit,
// even if the user hasn't made a single pick yet. Doesn't touch app state
// directly — the caller (Overview) re-dispatches getStatusAction right after
// this resolves, so entryPage/focus/etc. refresh from the single
// known-correct source (fetchUserStatus's response shape), rather than this
// action trying to parse enter_contest's differently-shaped response.
export const enterContestAction = createAsyncThunk(
  "app/enterContest",
  async ({ userId, contestId }, { rejectWithValue }) => {
    try {
      const res = await enterContest({
        user_id: userId,
        contest_id: contestId,
      });

      return res;
    } catch (err) {
      console.error("Error in [enterContest] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setTimezone(state) {
      const tz = moment.tz.guess();
      state.tz = tz;
    },
    setAuthResolved(state) {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setBackgroundImageAction.fulfilled, (state, action) => {
      state.backgroundImage = action.payload;
    });

    builder.addCase(getStatusAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getStatusAction.fulfilled, (state, action) => {
      const { contest_status, user_status, games_window, user_id, score, scoring } =
        action?.payload;
      const isGameLive = (games_window || []).some(
        (game) => game?.game_status === "InProgress",
      );

      state.userId = user_id;
      state.score = score || {
        earned_score: 0,
        missed_score: 0,
        max_score: 0,
        total_picks: 0,
        correct_picks: 0,
        missed_picks: 0,
      };
      state.scoring = scoring || null;
      state.playStartsAt = contest_status.play_starts_at;
      state.currentStage = contest_status.current_stage;
      state.contestType = contest_status.contest_type || "World Cup";
      state.entryPage = user_status.entry_page;
      state.focus = user_status.focus;
      state.upcomingGames = games_window;
      state.hasLiveGame = isGameLive;

      //group stage
      state.picksProgress.completedGroups = user_status.completed_groups;
      state.picksProgress.allGroupsComplete =
        user_status.all_groups_picks_complete;
      state.picksProgress.thirdPlaceComplete =
        user_status.third_place_picks_complete;
      state.picksProgress.groupStageComplete = user_status.group_stage_complete;
      //ko stage
      state.picksProgress.completedKORounds = user_status.completed_rounds;
      state.picksProgress.tiebreakerAnswer = user_status.tiebreaker_answer;
      state.picksProgress.allPicksComplete = user_status.all_picks_complete;
      //final stage
      state.picksProgress.picksSubmitted = user_status.picks_submitted;

      state.loading = false;
    });

    builder.addCase(getStatusAction.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { setTimezone, setAuthResolved } = appSlice.actions;

export default appSlice.reducer;
